/**
 * Observations Actions
 *
 * Actions for managing observations state in the NGXS store.
 */

import { Observation } from 'src/app/models/schema';

/**
 * Action to initialize the observations store
 * Loads the first batch of observations from the partner's subcollection
 * @param partnerId - Partner ID (determines subcollection path: partners/{partnerId}/observations)
 */
export class InitializeObservations {
  static readonly type = '[Observations] Initialize';
  constructor(public partnerId: string) {}
}

/**
 * Action dispatched when initialization succeeds
 * @param observations - The initial batch of observations
 * @param hasMore - Whether there are more observations to load
 */
export class InitializeObservationsSuccess {
  static readonly type = '[Observations] Initialize Success';
  constructor(
    public observations: Observation[],
    public hasMore: boolean
  ) {}
}

/**
 * Action dispatched when initialization fails
 * @param error - The error message
 */
export class InitializeObservationsFailure {
  static readonly type = '[Observations] Initialize Failure';
  constructor(public error: string) {}
}

/**
 * Action to load the next batch of observations
 */
export class LoadNextBatch {
  static readonly type = '[Observations] Load Next Batch';
}

/**
 * Action dispatched when loading next batch succeeds
 * @param observations - The new batch of observations
 * @param hasMore - Whether there are more observations to load
 */
export class LoadNextBatchSuccess {
  static readonly type = '[Observations] Load Next Batch Success';
  constructor(
    public observations: Observation[],
    public hasMore: boolean
  ) {}
}

/**
 * Action dispatched when loading next batch fails
 * @param error - The error message
 */
export class LoadNextBatchFailure {
  static readonly type = '[Observations] Load Next Batch Failure';
  constructor(public error: string) {}
}

/**
 * Action to set filter criteria
 * @param filters - Filter options to apply
 */
export class SetObservationsFilters {
  static readonly type = '[Observations] Set Filters';
  constructor(public filters: any) {}
}

/**
 * Action to set sort criteria
 * @param sortField - Field to sort by
 * @param sortDirection - Sort direction (asc/desc)
 */
export class SetObservationsSort {
  static readonly type = '[Observations] Set Sort';
  constructor(
    public sortField: string,
    public sortDirection: 'asc' | 'desc'
  ) {}
}

/**
 * Action to add a new observation entry
 * @param observation - The observation to add
 */
export class AddObservation {
  static readonly type = '[Observations] Add Observation';
  constructor(public observation: Observation) {}
}

/**
 * Action dispatched when adding observation succeeds
 * @param observation - The added observation
 */
export class AddObservationSuccess {
  static readonly type = '[Observations] Add Observation Success';
  constructor(public observation: Observation) {}
}

/**
 * Action dispatched when adding observation fails
 * @param error - The error message
 */
export class AddObservationFailure {
  static readonly type = '[Observations] Add Observation Failure';
  constructor(public error: string) {}
}

/**
 * Action to update observations from real-time listener
 * @param observations - Updated observations from Firestore
 */
export class UpdateObservationsFromListener {
  static readonly type = '[Observations] Update From Listener';
  constructor(public observations: Observation[]) {}
}

/**
 * Action to clear all observations data
 * Used when switching teams or cleaning up
 */
export class ClearObservations {
  static readonly type = '[Observations] Clear';
}

/**
 * Action to clear error state
 */
export class ClearObservationsError {
  static readonly type = '[Observations] Clear Error';
}
